"use client";

import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";

type ProjectMediaCarouselProps = {
  imageUrls: string[];
  videoUrls: string[];
  title: string;
};

type Slide = {
  type: "image" | "video";
  url: string;
};

export function ProjectMediaCarousel({ imageUrls, videoUrls, title }: ProjectMediaCarouselProps) {
  const t = useTranslations("MediaCarousel");
  const slides = useMemo<Slide[]>(
    () => [...imageUrls.map((url) => ({ type: "image" as const, url })), ...videoUrls.map((url) => ({ type: "video" as const, url }))],
    [imageUrls, videoUrls]
  );
  const [activeIndex, setActiveIndex] = useState(0);

  if (slides.length === 0) {
    return (
      <div className="rounded-2xl border border-zinc-300 bg-white/85 p-6 text-sm text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900/70 dark:text-zinc-400">
        {t("none")}
      </div>
    );
  }

  const currentSlide = slides[activeIndex];

  return (
    <section className="overflow-hidden rounded-2xl border border-zinc-300 bg-white/85 dark:border-zinc-800 dark:bg-zinc-900/70">
      <div className="relative aspect-video bg-zinc-100 dark:bg-zinc-950">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${currentSlide.type}-${currentSlide.url}`}
            initial={{ opacity: 0.15, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0.2 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="absolute inset-0"
          >
            {currentSlide.type === "image" ? (
              // Banner media can be any external URL; use a plain image with object-cover.
              // eslint-disable-next-line @next/next/no-img-element
              <img src={currentSlide.url} alt={`${title} media`} className="h-full w-full object-cover" />
            ) : (
              <video src={currentSlide.url} className="h-full w-full object-cover" controls autoPlay muted loop playsInline preload="metadata" />
            )}
          </motion.div>
        </AnimatePresence>

        {slides.length > 1 ? (
          <>
            <button
              type="button"
              aria-label={t("prev")}
              title={t("prev")}
              onClick={() => setActiveIndex((prev) => (prev - 1 + slides.length) % slides.length)}
              className="absolute left-3 top-1/2 z-10 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-zinc-300 bg-white/85 text-zinc-800 backdrop-blur transition hover:border-zinc-400 hover:bg-zinc-100 dark:border-zinc-600 dark:bg-zinc-950/75 dark:text-zinc-100 dark:hover:border-zinc-400 dark:hover:bg-zinc-900"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label={t("next")}
              title={t("next")}
              onClick={() => setActiveIndex((prev) => (prev + 1) % slides.length)}
              className="absolute right-3 top-1/2 z-10 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-zinc-300 bg-white/85 text-zinc-800 backdrop-blur transition hover:border-zinc-400 hover:bg-zinc-100 dark:border-zinc-600 dark:bg-zinc-950/75 dark:text-zinc-100 dark:hover:border-zinc-400 dark:hover:bg-zinc-900"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <p className="absolute bottom-3 left-1/2 z-10 -translate-x-1/2 rounded-full border border-zinc-300 bg-white/90 px-2.5 py-1 text-[11px] text-zinc-700 backdrop-blur dark:border-zinc-700 dark:bg-zinc-950/80 dark:text-zinc-200">
              {activeIndex + 1}/{slides.length}
            </p>
          </>
        ) : null}
      </div>
    </section>
  );
}
